import { useState } from 'react';
import { formatDateLong, formatDate, formatTime } from '../utils/dateUtils';
import { Helmet } from 'react-helmet-async';
import env from '../config/env';
import { mockOrders } from '../data/mockOrders';
import apiClient from '../api/client';
import { isUnpaidOnlineOrder, UNPAID_LABEL } from '../utils/orderStatus';

const trackOrder = async (orderId, email) => {
  if (env.isMock) {
    const found = mockOrders.find(
      (o) => o.id.toLowerCase() === orderId.trim().toLowerCase() &&
             o.userId.toLowerCase() === email.trim().toLowerCase()
    );
    return found || null;
  }
  try {
    // Backend: GET /track/{orderNumber}?email=
    const { data } = await apiClient.get(`/track/${encodeURIComponent(orderId.trim())}`, {
      params: { email: email.trim() },
    });
    return data;
  } catch {
    return null;
  }
};

// What the customer sees for each order status ("Unfulfilled" is the store's word for a confirmed order not yet sent)
const TRACK_STATUS = {
  unfulfilled: { label: 'Confirmed', cls: 'bg-violet-100 text-violet-700' },
  processing: { label: 'Processing', cls: 'bg-yellow-100 text-yellow-700' },
  shipped: { label: 'Shipped', cls: 'bg-blue-100 text-blue-700' },
  delivered: { label: 'Delivered', cls: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-600' },
};

const TrackOrderPage = () => {
  const [orderId, setOrderId] = useState('');
  const [orderEmail, setOrderEmail] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const found = await trackOrder(orderId, orderEmail);
    setLoading(false);
    if (found) setResult(found);
    else {
      setError(env.isMock
        ? 'Order not found. Try: Order ID = SKT-2024-001, Email = demo@skinnthrive.com'
        : 'Order not found. Please check your Order ID and email address.');
      setResult(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Track Your Order — Skinn Thrive</title>
        <meta name="description" content="Track your Skinn Thrive order with your Order ID and email address." />
      </Helmet>

      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Track Your Order</h1>
          <p className="text-gray-500">Enter your Order ID and email to see real-time updates.</p>
        </div>

        <div className="card p-6 mb-8">
          <form onSubmit={handleTrack} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
              <input type="text" value={orderId} onChange={e => setOrderId(e.target.value)} placeholder="e.g. SKT-2024-001" className="input-field" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input type="email" value={orderEmail} onChange={e => setOrderEmail(e.target.value)} placeholder="email used at checkout" className="input-field" required />
            </div>
            {error && <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{error}</p>}
            <button type="submit" disabled={loading} className="w-full btn-primary py-3.5">
              {loading ? 'Tracking...' : 'Track Order'}
            </button>
          </form>
          {env.isMock && (
            <p className="text-xs text-gray-400 text-center mt-3">Demo: ID = SKT-2024-001, Email = demo@skinnthrive.com</p>
          )}
        </div>

        {result && (
          <div className="card p-6 animate-fade-in">
            <div className="flex justify-between items-start mb-5">
              <div>
                <h2 className="font-bold text-gray-900 text-lg">Order {result.orderNumber || result.id}</h2>
                <p className="text-sm text-gray-500">Placed on {formatDateLong(result.createdAt || result.placedAt)}</p>
              </div>
              {(() => {
                // The API sends fulfillmentStatus (e.g. UNFULFILLED); mock data sends status (e.g. "delivered")
                const key = String(result.fulfillmentStatus || result.status || '').toLowerCase();
                const { label, cls } = isUnpaidOnlineOrder(result)
                  ? { label: UNPAID_LABEL, cls: 'bg-amber-100 text-amber-700' }
                  : TRACK_STATUS[key] || { label: key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Confirmed', cls: 'bg-yellow-100 text-yellow-700' };
                return <span className={`badge text-sm px-3 py-1.5 ${cls}`}>{label}</span>;
              })()}
            </div>

            {/* Items */}
            {result.items?.length > 0 && (
              <div className="mb-6">
                {result.items.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0">
                    <img src={item.imageUrl || item.image} alt={item.title} className="w-14 h-14 rounded-lg object-cover bg-gray-100" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.style.visibility = 'hidden'; }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 line-clamp-1">{item.title}</p>
                      <p className="text-xs text-gray-400">Qty: {item.quantity}</p>
                    </div>
                    <p className="text-sm font-bold text-gray-900">₹{(item.price || 0) * item.quantity}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Tracking Timeline */}
            {result.tracking?.events?.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-800 mb-4">Tracking Timeline</h3>
                <div className="relative">
                  {result.tracking.events.map((event, i) => {
                    const isLast = i === result.tracking.events.length - 1;
                    // Support both frontend format {date, time} and backend format {eventTime, message}
                    let displayDate = event.date || '';
                    let displayTime = event.time || '';
                    if (!displayDate && event.eventTime) {
                      displayDate = formatDate(event.eventTime);
                      displayTime = formatTime(event.eventTime);
                    }
                    const displayLabel = event.message || event.status;
                    return (
                      <div key={i} className="flex gap-4 pb-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-4 h-4 rounded-full border-2 shrink-0 mt-0.5 ${isLast ? 'bg-primary border-primary' : 'bg-green-500 border-green-500'}`} />
                          {!isLast && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
                        </div>
                        <div className={`pb-1 ${isLast ? '' : 'mb-2'}`}>
                          <p className={`text-sm font-semibold ${isLast ? 'text-primary' : 'text-gray-700'}`}>{displayLabel}</p>
                          <p className="text-xs text-gray-400">{event.location}{(displayDate || displayTime) ? ` · ${displayDate} ${displayTime}` : ''}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {result.tracking?.trackingId && (
              <p className="text-xs text-gray-400">Tracking ID: {result.tracking.trackingId} via {result.tracking.carrier}</p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default TrackOrderPage;
